import React, { useEffect, useRef, useState } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { TbCheck, TbChevronDown } from 'react-icons/tb';
import { ISelectOption } from 'types';
import { wrap, trigger, label, arrow, menu, item } from '../MultiSelect/style';
import { selectedItem, checkMark } from './style';

interface Props<T extends string> {
  options: ISelectOption<T>[];
  value: T | '';
  handleChange: (value: T | '') => void;
  title?: string;
  allLabel?: string;
  css?: Interpolation<Theme>;
}

function Dropdown<T extends string>({
  options,
  value,
  handleChange,
  title,
  allLabel = '전체',
  css,
}: Props<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const summary = options.find((o) => o.value === value)?.name ?? allLabel;

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const handleKey = (e: KeyboardEvent) =>
      e.key === 'Escape' && setIsOpen(false);
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  const select = (v: T | '') => {
    handleChange(v);
    setIsOpen(false);
  };

  const items: { name: string; value: T | ''; disabled?: boolean }[] = [
    { name: allLabel, value: '' },
    ...options,
  ];

  return (
    <div css={[wrap, css]} ref={ref}>
      <button
        type="button"
        css={trigger(isOpen, value !== '')}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {title && <span css={label}>{title}</span>}
        <span>{summary}</span>
        <TbChevronDown size={16} css={arrow(isOpen)} />
      </button>
      {isOpen && (
        <ul css={menu} role="listbox">
          {items.map((o) => {
            const isSelected = o.value === value;
            return (
              <li
                key={o.value || '__all'}
                css={[item, isSelected && selectedItem]}
                role="option"
                aria-selected={isSelected}
                aria-disabled={o.disabled}
                onClick={() => !o.disabled && select(o.value)}
              >
                {o.name}
                {isSelected && <TbCheck size={16} css={checkMark} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default Dropdown;
